import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import BusinessIcon from "@mui/icons-material/BusinessOutlined"
import BuildIcon from "@mui/icons-material/BuildOutlined"
import WarningIcon from "@mui/icons-material/WarningAmberOutlined"

import { clientApi } from "../api"
import { usePermissions } from "../permissions"
import PageHeader from "../components/PageHeader"
import EmptyState from "../components/EmptyState"
import { LoadingRows } from "../components/LoadingState"
import AddClientDialog from "./clients/AddClientDialog"
import ClientDetailDialog from "./clients/ClientDetailDialog"
import UserAvatar from "../components/UserAvatar"
import { formatDate } from "../utils/format"
import { rise } from "../motion"

const STATUS_COLOR = { active: "success", suspended: "warning", archived: "default" }

/**
 * The client list — the multi-tenancy surface.
 *
 * Reachable only with `clients:view`, which no client-side role holds. The
 * sidebar hides it otherwise; this page does not need its own check because the
 * route is never rendered for someone who cannot reach it.
 */
export default function ClientsPage() {
  const { can } = usePermissions()
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [addOpen, setAddOpen] = useState(false)
  const [provisioning, setProvisioning] = useState(null)
  const [openClientId, setOpenClientId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await clientApi.list()
      setClients(data.organizations || [])
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const provision = async (id) => {
    setProvisioning(id)
    try {
      await clientApi.provision(id)
      await load()
    } catch (err) {
      setError(err.userMessage)
    } finally {
      setProvisioning(null)
    }
  }

  const unprovisioned = clients.filter((c) => !c.schema_provisioned && c.id !== "0")

  return (
    <Box>
      <PageHeader
        title="Clients"
        subtitle="Every organization on the platform. Adding one registers it and builds its database."
        actions={
          can("clients", "create") ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add client
            </Button>
          ) : null
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {/* Surfaced rather than left to be discovered: a client registered without a
          schema looks fine on this list until somebody tries to use it. */}
      {unprovisioned.length > 0 ? (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          {unprovisioned.length} client{unprovisioned.length === 1 ? " has" : "s have"} no database yet. Use the build
          icon to provision {unprovisioned.length === 1 ? "it" : "them"}.
        </Alert>
      ) : null}

      <Card>
        {loading ? (
          <Box sx={{ p: 2 }}>
            <LoadingRows rows={5} />
          </Box>
        ) : clients.length === 0 ? (
          <EmptyState
            icon={BusinessIcon}
            title="No clients yet"
            description="Add your first client to give them a database, users and their own workspace."
            actionLabel={can("clients", "create") ? "Add client" : null}
            onAction={() => setAddOpen(true)}
          />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Id / schema</TableCell>
                  <TableCell>Industry</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right">Projects</TableCell>
                  <TableCell align="right">Open tasks</TableCell>
                  <TableCell>Added</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client, index) => (
                  <TableRow key={client.id} hover sx={rise(index)}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {/* The name opens the client, the same way a project's
                            name opens the project everywhere else. */}
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => setOpenClientId(client.id)}
                          sx={{ fontWeight: 600, color: "primary.dark", textAlign: "left" }}
                        >
                          {client.label || client.name}
                        </Link>
                        {client.id === "0" ? <Chip size="small" label="Back office" color="primary" sx={{ height: 20 }} /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {/* The id IS the MySQL schema name, so it is shown as code
                          rather than as a label — operators grep for it. */}
                      <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                        {client.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* Industry is what the client does. A wireless dealer's
                          carrier is something else — it decides their tables —
                          and rides underneath only where there is one. */}
                      <Typography variant="body2" color={client.industry ? "text.primary" : "text.secondary"}>
                        {client.industry || "—"}
                      </Typography>
                      {client.carrier_label ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {client.carrier_label} dealer
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {client.owner_name ? (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <UserAvatar name={client.owner_name} size={22} tooltip={false} />
                          <Typography variant="body2" noWrap>
                            {client.owner_name}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Chip size="small" label={client.status} color={STATUS_COLOR[client.status] || "default"} sx={{ height: 22 }} />
                        {!client.schema_provisioned && client.id !== "0" ? (
                          <Chip size="small" label="no database" color="warning" variant="outlined" sx={{ height: 22 }} />
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{client.user_count ?? 0}</TableCell>
                    <TableCell align="right">{client.project_count ?? 0}</TableCell>
                    <TableCell align="right">{client.open_task_count ?? 0}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(client.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {can("clients", "provision_schema") && client.id !== "0" ? (
                        <Tooltip title={client.schema_provisioned ? "Rebuild this client's schema" : "Build this client's schema"}>
                          <span>
                            <IconButton size="small" disabled={provisioning === client.id} onClick={() => provision(client.id)}>
                              <BuildIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>

      <ClientDetailDialog clientId={openClientId} onClose={() => setOpenClientId(null)} onChanged={load} />

      <AddClientDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => {
          setAddOpen(false)
          load()
        }}
      />
    </Box>
  )
}
